import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TableSortLabel,
    TextField,
    Box,
    Typography,
    Checkbox,
    IconButton,
    Tooltip,
    Chip,
    InputAdornment,
    LinearProgress,
    Skeleton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * DataTable component for displaying data in a table format
 * @param {Object} props - Component props
 * @param {Array} props.columns - Array of column definitions
 * @param {Array} props.data - Array of data objects
 * @param {Boolean} props.loading - Whether data is loading
 * @param {Function} props.onRowClick - Function called when a row is clicked
 * @param {Boolean} props.selectable - Whether rows are selectable
 * @param {Array} props.selectedRows - Array of selected row IDs
 * @param {Function} props.onSelectionChange - Function called when selection changes
 * @param {String} props.idField - Field to use as ID for rows
 * @param {Function} props.onRefresh - Function to refresh data
 * @param {Boolean} props.searchable - Whether to show search field
 * @returns {JSX.Element} DataTable component
 */
const DataTable = ({
    columns = [],
    data = [],
    loading = false,
    onRowClick = null,
    selectable = false,
    selectedRows = [],
    onSelectionChange = null,
    idField = 'id',
    onRefresh = null,
    searchable = true,
    title = '',
}) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState(columns[0]?.field || '');
    const [order, setOrder] = useState('asc');
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredData, setFilteredData] = useState([]);

    // Filter and sort data when dependencies change
    useEffect(() => {
        let filtered = [...data];

        // Apply search filter
        if (searchQuery) {
            const lowercasedQuery = searchQuery.toLowerCase();
            filtered = filtered.filter(item => {
                return columns.some(column => {
                    const value = item[column.field];
                    if (value === null || value === undefined) return false;
                    return String(value).toLowerCase().includes(lowercasedQuery);
                });
            });
        }

        // Apply sorting
        if (orderBy) {
            filtered = filtered.sort((a, b) => {
                const valueA = a[orderBy] === null || a[orderBy] === undefined ? '' : a[orderBy];
                const valueB = b[orderBy] === null || b[orderBy] === undefined ? '' : b[orderBy];

                if (valueA < valueB) {
                    return order === 'asc' ? -1 : 1;
                }
                if (valueA > valueB) {
                    return order === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        setFilteredData(filtered);
        setPage(0); // Reset to first page when filter changes
    }, [data, searchQuery, orderBy, order, columns]);

    // Handle column sorting
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Handle pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle row selection
    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = filteredData.map(n => n[idField]);
            onSelectionChange(newSelected);
            return;
        }
        onSelectionChange([]);
    };

    const handleRowSelect = (event, id) => {
        const selectedIndex = selectedRows.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedRows, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedRows.slice(1));
        } else if (selectedIndex === selectedRows.length - 1) {
            newSelected = newSelected.concat(selectedRows.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedRows.slice(0, selectedIndex),
                selectedRows.slice(selectedIndex + 1),
            );
        }

        onSelectionChange(newSelected);
    };

    const isSelected = (id) => selectedRows.indexOf(id) !== -1;

    // Calculate empty rows to maintain consistent table height
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredData.length) : 0;

    // Slice data for current page
    const visibleRows = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Render cell content based on column type
    const renderCellContent = (column, value, row) => {
        if (column.renderCell) {
            return column.renderCell(value, row);
        }
        switch (column.type) {
            case 'date':
                return value ? new Date(value).toLocaleDateString() : '';
            case 'datetime':
                return value ? new Date(value).toLocaleString() : '';
            case 'boolean':
                return value ? 'Yes' : 'No';
            case 'chip':
                return value ? (
                    <Chip
                        label={value}
                        color={column.valueColorMap?.[value] || 'default'}
                        size="small"
                    />
                ) : '';
            case 'currency':
                return value !== null && value !== undefined
                    ? new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(value)
                    : '';
            case 'number':
                return value !== null && value !== undefined
                    ? new Intl.NumberFormat('th-TH').format(value)
                    : '';
            default:
                return value !== null && value !== undefined ? String(value) : '';
        }
    };

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            {/* Table Header with Search and Actions */}
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {title && (
                    <Typography variant="h6" component="div">
                        {title}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                    {searchable && (
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="ค้นหา..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}

                    {onRefresh && (
                        <Tooltip title="รีเฟรช">
                            <IconButton onClick={onRefresh}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Loading indicator */}
            {loading && (
                <LinearProgress color="primary" />
            )}

            <TableContainer sx={{ maxHeight: 'calc(100vh - 240px)' }}>
                <Table stickyHeader aria-label="data table">
                    {/* Table Header */}
                    <TableHead>
                        <TableRow>
                            {/* Selection checkbox column */}
                            {selectable && onSelectionChange && (
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        color="primary"
                                        indeterminate={selectedRows.length > 0 && selectedRows.length < filteredData.length}
                                        checked={filteredData.length > 0 && selectedRows.length === filteredData.length}
                                        onChange={handleSelectAllClick}
                                    />
                                </TableCell>
                            )}

                            {/* Table column headers */}
                            {columns.map((column) => (
                                <TableCell
                                    key={column.field}
                                    align={column.align || (column.type === 'number' || column.type === 'currency' ? 'right' : 'left')}
                                    padding={column.disablePadding ? 'none' : 'normal'}
                                    sortDirection={orderBy === column.field ? order : false}
                                    style={{
                                        minWidth: column.minWidth,
                                        width: column.width,
                                        whiteSpace: 'nowrap',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    <TableSortLabel
                                        active={orderBy === column.field}
                                        direction={orderBy === column.field ? order : 'asc'}
                                        onClick={() => handleRequestSort(column.field)}
                                    >
                                        {column.headerName}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    {/* Table Body */}
                    <TableBody>
                        {/* Loading state placeholders */}
                        {loading ? (
                            Array.from(new Array(rowsPerPage)).map((_, index) => (
                                <TableRow key={`skeleton-${index}`}>
                                    {selectable && <TableCell padding="checkbox"><Skeleton variant="rectangular" width={24} height={24} /></TableCell>}
                                    {columns.map((column, colIndex) => (
                                        <TableCell key={`skeleton-cell-${index}-${colIndex}`}>
                                            <Skeleton variant="text" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : visibleRows.length > 0 ? (
                            visibleRows.map((row, rowIndex) => {
                                const rowId = row[idField];
                                const isRowSelected = selectable ? isSelected(rowId) : false;

                                return (
                                    <TableRow
                                        hover
                                        role="checkbox"
                                        aria-checked={isRowSelected}
                                        tabIndex={-1}
                                        key={rowId || rowIndex}
                                        selected={isRowSelected}
                                        onClick={(event) => {
                                            if (onRowClick) onRowClick(row);
                                        }}
                                        sx={{
                                            cursor: onRowClick ? 'pointer' : 'default',
                                            '&.MuiTableRow-root.Mui-selected': {
                                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                            },
                                        }}
                                    >
                                        {/* Checkbox for row selection */}
                                        {selectable && onSelectionChange && (
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    color="primary"
                                                    checked={isRowSelected}
                                                    onChange={(event) => handleRowSelect(event, rowId)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </TableCell>
                                        )}

                                        {/* Data cells */}
                                        {columns.map((column) => {
                                            const value = row[column.field];
                                            return (
                                                <TableCell
                                                    key={column.field}
                                                    align={column.align || (column.type === 'number' || column.type === 'currency' ? 'right' : 'left')}
                                                    sx={{
                                                        color: column.valueColorMap ? column.valueColorMap[value] : 'inherit',
                                                        ...column.cellStyle,
                                                    }}
                                                >
                                                    {renderCellContent(column, value, row)}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })
                        ) : (
                            // Empty state
                            <TableRow style={{ height: 53 * (emptyRows || 1) }}>
                                <TableCell
                                    colSpan={columns.length + (selectable ? 1 : 0)}
                                    align="center"
                                >
                                    <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="subtitle1" color="text.secondary">
                                            ไม่พบข้อมูล
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}

                        {/* Empty rows to maintain table height */}
                        {emptyRows > 0 && !loading && visibleRows.length > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={columns.length + (selectable ? 1 : 0)} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Table Pagination */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="แสดง:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
            />
        </Paper>
    );
};

DataTable.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            headerName: PropTypes.string.isRequired,
            width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            align: PropTypes.oneOf(['left', 'right', 'center']),
            type: PropTypes.oneOf(['string', 'number', 'date', 'datetime', 'boolean', 'chip', 'currency']),
            renderCell: PropTypes.func,
            valueColorMap: PropTypes.object,
            cellStyle: PropTypes.object,
        })
    ).isRequired,
    data: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    onRowClick: PropTypes.func,
    selectable: PropTypes.bool,
    selectedRows: PropTypes.array,
    onSelectionChange: PropTypes.func,
    idField: PropTypes.string,
    onRefresh: PropTypes.func,
    searchable: PropTypes.bool,
    title: PropTypes.string,
};

export default DataTable;